import type { BrowserCommentTarget } from "@cmmd-forge/contracts";
import { normalizePreviewUrl } from "~/lib/devServerPreviewUrl";
import { isBlankBrowserUrl } from "./BrowserPreviewPanel.url";

export interface BrowserScreenshotAttachmentMetadata {
  readonly mimeType: string;
  readonly hostname: string;
  readonly name: string;
}

const BROWSER_SCREENSHOT_DATA_URL_PATTERN =
  /^data:image\/[a-z0-9.+-]+;base64,([a-z0-9+/]+={0,2})$/i;

export type BrowserCommentSubmitAction = "blocked" | "needs-fresh-capture" | "dispatch-comment";

export type BrowserAnnotationToggleAction = "needs-capture" | "clear-selection" | "start-selection";

export interface BrowserViewportSyncDecision {
  readonly action: "ignore" | "commit" | "sync";
  readonly viewport: { readonly width: number; readonly height: number };
}

export function buildBrowserScreenshotAttachmentMetadata(input: {
  readonly dataUrl: string;
  readonly url: string | null;
  readonly nowMs: number;
}): BrowserScreenshotAttachmentMetadata {
  const mimeType = input.dataUrl.match(/^data:([^;,]+)/)?.[1] ?? "image/png";
  const hostname = input.url
    ? (() => {
        try {
          return new URL(input.url).hostname.replace(/[^a-z0-9.-]+/gi, "-");
        } catch {
          return "browser";
        }
      })()
    : "browser";
  return {
    mimeType,
    hostname,
    name: `browser-${hostname || "capture"}-${input.nowMs}.png`,
  };
}

export function hasUsableBrowserScreenshotDataUrl(dataUrl: string): boolean {
  const payload = dataUrl.match(BROWSER_SCREENSHOT_DATA_URL_PATTERN)?.[1];
  return Boolean(payload && payload.length > 0);
}

export function browserPreviewUrlsMatch(
  tabUrl: string | null | undefined,
  captureUrl: string | undefined,
): boolean {
  if (isBlankBrowserUrl(tabUrl) || !captureUrl) {
    return false;
  }
  const normalizedTabUrl = normalizePreviewUrl(tabUrl ?? "");
  const normalizedCaptureUrl = normalizePreviewUrl(captureUrl);
  if (normalizedTabUrl && normalizedCaptureUrl) {
    return normalizedTabUrl === normalizedCaptureUrl;
  }
  return captureUrl === tabUrl;
}

export function isFreshBrowserCapture(input: {
  readonly tabUrl: string | null | undefined;
  readonly captureId?: string | undefined;
  readonly expectedCaptureId?: string | undefined;
  readonly captureUrl: string | undefined;
  readonly capturedAt: string | undefined;
  readonly requestedAt: string | undefined;
  readonly sessionViewport?: { readonly width: number; readonly height: number } | null | undefined;
  readonly captureViewport?: { readonly width: number; readonly height: number } | null | undefined;
}): boolean {
  if (isBlankBrowserUrl(input.tabUrl) || !input.captureUrl || !input.capturedAt) {
    return false;
  }
  if (!browserPreviewUrlsMatch(input.tabUrl, input.captureUrl)) {
    return false;
  }
  if (
    input.sessionViewport &&
    input.captureViewport &&
    (input.sessionViewport.width !== input.captureViewport.width ||
      input.sessionViewport.height !== input.captureViewport.height)
  ) {
    return false;
  }
  if (input.expectedCaptureId && input.captureId === input.expectedCaptureId) {
    return true;
  }
  if (!input.requestedAt) {
    return true;
  }
  return Date.parse(input.capturedAt) >= Date.parse(input.requestedAt);
}

function isCapturedAtOrAfter(input: {
  readonly capturedAt: string | undefined;
  readonly requestedAt: string | undefined;
}): boolean {
  if (!input.requestedAt) {
    return true;
  }
  if (!input.capturedAt) {
    return false;
  }
  const capturedAt = Date.parse(input.capturedAt);
  const requestedAt = Date.parse(input.requestedAt);
  if (Number.isNaN(capturedAt) || Number.isNaN(requestedAt)) {
    return false;
  }
  return capturedAt >= requestedAt;
}

export function isBrowserFrameFresh(input: {
  readonly tabUrl: string | null | undefined;
  readonly frameUrl: string | undefined;
  readonly frameCapturedAt?: string | undefined;
  readonly requestedAt?: string | undefined;
  readonly sessionViewport: { readonly width: number; readonly height: number } | null | undefined;
  readonly frameViewport: { readonly width: number; readonly height: number } | null | undefined;
}): boolean {
  if (!browserPreviewUrlsMatch(input.tabUrl, input.frameUrl)) {
    return false;
  }
  if (
    !isCapturedAtOrAfter({
      capturedAt: input.frameCapturedAt,
      requestedAt: input.requestedAt,
    })
  ) {
    return false;
  }
  if (!input.sessionViewport || !input.frameViewport) {
    return true;
  }
  return (
    input.sessionViewport.width === input.frameViewport.width &&
    input.sessionViewport.height === input.frameViewport.height
  );
}

export function resolveBrowserCommentSubmitAction(input: {
  readonly hasServerThread: boolean;
  readonly target: BrowserCommentTarget | null;
  readonly note: string;
  readonly activeTabUrl: string | null;
  readonly activeTabId: string | null | undefined;
  readonly activeScreenshotUrl?: string | undefined;
  readonly activeScreenshotCapturedAt?: string | undefined;
  readonly sessionViewport?: { readonly width: number; readonly height: number } | null | undefined;
  readonly captureViewport?: { readonly width: number; readonly height: number } | null | undefined;
  readonly hasOrchestration: boolean;
}): BrowserCommentSubmitAction {
  if (!input.hasServerThread || !input.target || input.note.trim().length === 0) {
    return "blocked";
  }
  if (
    !input.activeTabUrl ||
    input.target.tabId !== input.activeTabId ||
    input.target.url !== input.activeTabUrl ||
    !isFreshBrowserCapture({
      tabUrl: input.activeTabUrl,
      captureUrl: input.activeScreenshotUrl,
      capturedAt: input.activeScreenshotCapturedAt,
      requestedAt: undefined,
      sessionViewport: input.sessionViewport,
      captureViewport: input.captureViewport,
    })
  ) {
    return "needs-fresh-capture";
  }
  return input.hasOrchestration ? "dispatch-comment" : "blocked";
}

export function resolveBrowserAnnotationToggleAction(input: {
  readonly canAnnotate: boolean;
  readonly selecting: boolean;
}): BrowserAnnotationToggleAction {
  if (!input.canAnnotate) {
    return "needs-capture";
  }
  return input.selecting ? "clear-selection" : "start-selection";
}

export function resolveBrowserViewportSyncDecision(input: {
  readonly width: number;
  readonly height: number;
  readonly hasBrowserSession: boolean;
  readonly sessionViewport: { readonly width: number; readonly height: number } | null;
}): BrowserViewportSyncDecision {
  const viewport = {
    width: Math.round(input.width),
    height: Math.round(input.height),
  };
  if (viewport.width < 200 || viewport.height < 200 || !input.hasBrowserSession) {
    return { action: "ignore", viewport };
  }
  if (
    input.sessionViewport &&
    input.sessionViewport.width === viewport.width &&
    input.sessionViewport.height === viewport.height
  ) {
    return { action: "commit", viewport };
  }
  return { action: "sync", viewport };
}
